import {
  type DefaultError,
  useMutation,
  useQuery,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import type { IAvatarAddResponse, IAvatarGetResponse } from '@/types/services/avatar.service';
import useAuthenticatedQuery from './useAuthenticatedQuery';

const useAvatarService = () => {
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();

  const getAvatar = (): UseQueryReturnType<IAvatarGetResponse | null, DefaultError> =>
    useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'getAvatar'],
      queryFn: async ({ signal }) => {
        const res = await axiosInstance
          .get(`/u/${userIndex.value}/details/avatar`, { signal })
          .catch(() => {
            return null;
          });

        if (!res) {
          return null;
        }
        return res.data;
      },
      enabled: queriesEnabled
    });

  const uploadAvatar = () => {
    return useMutation<IAvatarAddResponse, DefaultError, FormData>({
      mutationKey: ['authenticatedQuery', userIndex, 'uploadAvatar'],
      mutationFn: async (data) => {
        return (
          await axiosInstance.post<IAvatarAddResponse>(`/u/${userIndex.value}/details/avatar`, data)
        ).data;
      }
    });
  };

  const deleteAvatar = () => {
    return useMutation<void, DefaultError>({
      mutationKey: ['authenticatedQuery', userIndex, 'deleteAvatar'],
      mutationFn: async () => {
        await axiosInstance.delete(`/u/${userIndex.value}/details/avatar`);
      }
    });
  };
  return { getAvatar, uploadAvatar, deleteAvatar };
};

export default useAvatarService;
